import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  Building2,
  FileText,
  BarChart3,
  Shield,
  ShieldCheck,
  Search,
  RefreshCw,
  UserPlus,
  Mail,
  CheckCircle2,
  Copy,
  AlertTriangle,
  Trash2,
  Plus,
  Eye,
  EyeOff,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { DashboardLayout } from '@/components/DashboardLayout';
import { CreateUserForm } from '@/components/Forms';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { formatDate, formatCurrency, ROUTE_PATHS } from '@/lib/index';
import type { Profile, Condominio, Analisi } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

interface AdminProfile extends Profile {
  condomini_count?: number;
  analisi_count?: number;
}

interface AccessoCondomino {
  id: string;
  condominio_id: string;
  username: string;
  password_hash: string;
  stato: 'attivo' | 'disabilitato';
  created_at: string;
  condominio?: Condominio;
}

interface NewAccessoForm {
  condominio_id: string;
  username: string;
  password: string;
}

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  value: string | number;
  label: string;
}

function StatCard({ icon: Icon, value, label }: StatCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold mt-2">{value}</p>
          </div>
          <div className="p-3 rounded-lg bg-primary/10">
            <Icon className="h-6 w-6 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Admin() {
  const { isSuperAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<AdminProfile[]>([]);
  const [condomini, setCondomini] = useState<Condominio[]>([]);
  const [analisi, setAnalisi] = useState<Analisi[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchUsers, setSearchUsers] = useState('');
  const [promoting, setPromoting] = useState<string | null>(null);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [resendingCredentials, setResendingCredentials] = useState<string | null>(null);
  const [credentialsResult, setCredentialsResult] = useState<{ email: string; password: string; emailSent: boolean } | null>(null);

  const [accessi, setAccessi] = useState<AccessoCondomino[]>([]);
  const [loadingAccessi, setLoadingAccessi] = useState(false);
  const [creatingAccesso, setCreatingAccesso] = useState(false);
  const [newAccesso, setNewAccesso] = useState<NewAccessoForm>({
    condominio_id: '',
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [deleteAccessoId, setDeleteAccessoId] = useState<string | null>(null);
  const [searchAccessi, setSearchAccessi] = useState('');
  const [deleteAnalisiId, setDeleteAnalisiId] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !isSuperAdmin) {
      navigate(ROUTE_PATHS.DASHBOARD);
    }
  }, [isSuperAdmin, authLoading, navigate]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const [profilesRes, condominiRes, analisiRes] = await Promise.all([
        supabase.from('profiles').select('*').order('created_at', { ascending: false }),
        supabase.from('condomini').select('*').order('created_at', { ascending: false }),
        supabase.from('analisi').select('*').order('created_at', { ascending: false }),
      ]);

      const allProfiles: AdminProfile[] = (profilesRes.data || []).map((p) => ({
        ...p,
        condomini_count: (condominiRes.data || []).filter((c) => c.user_id === p.id).length,
        analisi_count: (analisiRes.data || []).filter((a) => {
          const condo = (condominiRes.data || []).find((c) => c.id === a.condominio_id);
          return condo?.user_id === p.id;
        }).length,
      }));

      setProfiles(allProfiles);
      setCondomini(condominiRes.data || []);
      setAnalisi(analisiRes.data || []);
    } catch {
      toast.error('Errore nel caricamento dei dati admin');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessi = async () => {
    setLoadingAccessi(true);
    try {
      const { data, error } = await supabase
        .from('accessi_condomini')
        .select('*, condominio:condomini(*)')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setAccessi((data || []) as AccessoCondomino[]);
    } catch {
      toast.error('Errore nel caricamento degli accessi condòmini');
    } finally {
      setLoadingAccessi(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAdminData();
      fetchAccessi();
    }
  }, [isSuperAdmin]);

  const handleResendCredentials = async (profile: AdminProfile) => {
    if (!profile.email) return;
    setResendingCredentials(profile.id);
    try {
      const { data: result, error: fnErr } = await supabase.functions.invoke('resend-credentials', {
        body: { email: profile.email, full_name: profile.full_name || profile.email },
      });
      const res = result as { success?: boolean; email?: string; password?: string; email_sent?: boolean; error?: string } | null;
      if (res?.success) {
        setCredentialsResult({
          email: res.email || profile.email || '',
          password: res.password || '',
          emailSent: res.email_sent || false,
        });
        if (res.email_sent) {
          toast.success(`Credenziali inviate a ${profile.email}`);
        } else {
          toast.warning('Password resettata. Email non inviata — verifica il dominio Resend.');
        }
      } else {
        const errMsg = res?.error || (fnErr as { message?: string })?.message || 'Errore';
        toast.error(`Errore: ${errMsg}`);
      }
    } catch (err) {
      toast.error('Errore durante il reinvio credenziali');
    } finally {
      setResendingCredentials(null);
    }
  };

  const handleSetSuperadmin = async (userId: string) => {
    setPromoting(userId);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: 'superadmin' })
        .eq('id', userId);
      if (error) throw error;
      toast.success('Utente promosso a superadmin');
      await fetchAdminData();
    } catch {
      toast.error('Errore nella promozione');
    } finally {
      setPromoting(null);
    }
  };

  const handleSetAdmin = async (userId: string) => {
    setPromoting(userId);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: 'admin' })
        .eq('id', userId);
      if (error) throw error;
      toast.success('Utente degradato ad admin');
      await fetchAdminData();
    } catch {
      toast.error('Errore nel cambio ruolo');
    } finally {
      setPromoting(null);
    }
  };

  const handleCreateAccesso = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAccesso.condominio_id || !newAccesso.username || !newAccesso.password) {
      toast.error('Compila tutti i campi');
      return;
    }

    setCreatingAccesso(true);
    try {
      const { error } = await supabase.from('accessi_condomini').insert([
        {
          condominio_id: newAccesso.condominio_id,
          username: newAccesso.username,
          password_hash: newAccesso.password,
          stato: 'attivo',
        },
      ]);

      if (error) throw error;

      toast.success('Accesso condòmino creato con successo');
      setNewAccesso({ condominio_id: '', username: '', password: '' });
      await fetchAccessi();
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      toast.error(`Errore: ${msg}`);
    } finally {
      setCreatingAccesso(false);
    }
  };

  const handleDeleteAccesso = async () => {
    if (!deleteAccessoId) return;

    try {
      const { error } = await supabase
        .from('accessi_condomini')
        .delete()
        .eq('id', deleteAccessoId);

      if (error) throw error;

      toast.success('Accesso eliminato');
      await fetchAccessi();
    } catch {
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleteAccessoId(null);
    }
  };

  const handleDeleteAnalisi = async () => {
    if (!deleteAnalisiId) return;

    try {
      const { error } = await supabase
        .from('analisi')
        .delete()
        .eq('id', deleteAnalisiId);

      if (error) throw error;

      toast.success('Analisi eliminata');
      await fetchAdminData();
    } catch {
      toast.error("Errore nell'eliminazione dell'analisi");
    } finally {
      setDeleteAnalisiId(null);
    }
  };

  const filteredProfiles = profiles.filter((p) => {
    const q = searchUsers.toLowerCase();
    return (
      p.full_name?.toLowerCase().includes(q) ||
      p.email?.toLowerCase().includes(q)
    );
  });

  const filteredAccessi = accessi.filter((a) => {
    const q = searchAccessi.toLowerCase();
    return (
      a.username.toLowerCase().includes(q) ||
      a.condominio?.nome?.toLowerCase().includes(q)
    );
  });

  const totalRevenue = analisi
    .filter((a) => a.stato === 'completata')
    .reduce((sum, a) => sum + (a.totale_uscite || 0), 0);

  const analisiProblematiche = analisi.filter(
    (a) => a.stato === 'bozza' || a.stato === 'errore'
  );

  if (authLoading || loading) {
    return (
      <DashboardLayout title="Amministrazione">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
            <p className="text-muted-foreground">Caricamento pannello admin...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!isSuperAdmin) return null;

  return (
    <DashboardLayout title="Amministrazione">
      <div className="space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Pannello Superadmin</h1>
              <p className="text-muted-foreground mt-1">
                Gestione completa di tutti gli utenti e i dati della piattaforma
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => { fetchAdminData(); fetchAccessi(); }} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Aggiorna
          </Button>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <StatCard icon={Users} value={profiles.length} label="Utenti Totali" />
          <StatCard icon={Building2} value={condomini.length} label="Condomini Totali" />
          <StatCard icon={BarChart3} value={analisi.length} label="Analisi Totali" />
          <StatCard icon={FileText} value={formatCurrency(totalRevenue)} label="Volume Analizzato" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Tabs defaultValue="utenti">
            <TabsList className="mb-6">
              <TabsTrigger value="utenti" className="gap-2">
                <Users className="h-4 w-4" />
                Gestione Utenti
              </TabsTrigger>
              <TabsTrigger value="accessi" className="gap-2">
                <Shield className="h-4 w-4" />
                Accessi Condòmini
              </TabsTrigger>
              <TabsTrigger value="analisi" className="gap-2">
                <BarChart3 className="h-4 w-4" />
                Gestione Analisi
              </TabsTrigger>
            </TabsList>

            <TabsContent value="utenti">
              <div className="grid grid-cols-1 gap-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Lista Utenti</CardTitle>
                      <div className="flex items-center gap-3">
                        <div className="relative w-64">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Cerca utenti..."
                            value={searchUsers}
                            onChange={(e) => setSearchUsers(e.target.value)}
                            className="pl-9"
                          />
                        </div>
                        <Button onClick={() => setShowCreateUserDialog(true)} className="gap-2">
                          <UserPlus className="h-4 w-4" />
                          Nuovo Utente
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Nome</TableHead>
                            <TableHead className="text-center">Ruolo</TableHead>
                            <TableHead className="text-center">Condomini</TableHead>
                            <TableHead className="text-center">Analisi</TableHead>
                            <TableHead>Registrato</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredProfiles.map((profile) => (
                            <TableRow key={profile.id}>
                              <TableCell className="font-medium">
                                {profile.email || '—'}
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {profile.full_name || '—'}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge
                                  variant={profile.role === 'superadmin' ? 'default' : 'secondary'}
                                  className="gap-1"
                                >
                                  {profile.role === 'superadmin' ? (
                                    <ShieldCheck className="h-3 w-3" />
                                  ) : (
                                    <Shield className="h-3 w-3" />
                                  )}
                                  {profile.role === 'superadmin' ? 'Superadmin' : 'Admin'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center text-muted-foreground">
                                {profile.condomini_count || 0}
                              </TableCell>
                              <TableCell className="text-center text-muted-foreground">
                                {profile.analisi_count || 0}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {profile.created_at ? formatDate(profile.created_at) : '—'}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1 text-xs"
                                    disabled={resendingCredentials === profile.id}
                                    onClick={() => handleResendCredentials(profile)}
                                    title="Reimposta password e invia credenziali"
                                  >
                                    {resendingCredentials === profile.id ? (
                                      <RefreshCw className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Mail className="h-3 w-3" />
                                    )}
                                    Credenziali
                                  </Button>
                                  {profile.role === 'admin' ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-1 text-xs"
                                      disabled={promoting === profile.id}
                                      onClick={() => handleSetSuperadmin(profile.id)}
                                    >
                                      <ShieldCheck className="h-3 w-3" />
                                      Promuovi
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-1 text-xs text-muted-foreground"
                                      disabled={promoting === profile.id}
                                      onClick={() => handleSetAdmin(profile.id)}
                                    >
                                      <Shield className="h-3 w-3" />
                                      Degrada
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                          {filteredProfiles.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                                Nessun utente trovato
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="accessi">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Accessi Condòmini</CardTitle>
                        <div className="relative w-64">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Cerca accessi..."
                            value={searchAccessi}
                            onChange={(e) => setSearchAccessi(e.target.value)}
                            className="pl-9"
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {loadingAccessi ? (
                        <div className="flex items-center justify-center py-8">
                          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Username</TableHead>
                                <TableHead>Password</TableHead>
                                <TableHead>Condominio</TableHead>
                                <TableHead className="text-center">Stato</TableHead>
                                <TableHead>Creato</TableHead>
                                <TableHead className="text-right">Azioni</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredAccessi.map((accesso) => (
                                <TableRow key={accesso.id}>
                                  <TableCell className="font-medium font-mono">
                                    {accesso.username}
                                  </TableCell>
                                  <TableCell className="font-mono text-sm text-muted-foreground">
                                    {accesso.password_hash}
                                  </TableCell>
                                  <TableCell>
                                    {accesso.condominio?.nome || '—'}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Badge
                                      variant={accesso.stato === 'attivo' ? 'default' : 'secondary'}
                                    >
                                      {accesso.stato === 'attivo' ? 'Attivo' : 'Disabilitato'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {formatDate(accesso.created_at)}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="gap-1 text-destructive hover:text-destructive"
                                      onClick={() => setDeleteAccessoId(accesso.id)}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                      Elimina
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {filteredAccessi.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                                    Nessun accesso trovato
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Plus className="h-5 w-5 text-primary" />
                        Crea Accesso Condòmino
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Crea credenziali per l'accesso al portale condòmini.
                      </p>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleCreateAccesso} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="acc-condo">Condominio *</Label>
                          <Select
                            value={newAccesso.condominio_id || 'none'}
                            onValueChange={(value) =>
                              setNewAccesso((p) => ({
                                ...p,
                                condominio_id: value === 'none' ? '' : value,
                              }))
                            }
                            disabled={creatingAccesso}
                          >
                            <SelectTrigger id="acc-condo">
                              <SelectValue placeholder="Seleziona condominio" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Seleziona condominio</SelectItem>
                              {condomini.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="acc-user">Username *</Label>
                          <Input
                            id="acc-user"
                            placeholder="es. condominio123"
                            value={newAccesso.username}
                            onChange={(e) =>
                              setNewAccesso((p) => ({ ...p, username: e.target.value }))
                            }
                            disabled={creatingAccesso}
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="acc-pwd">Password *</Label>
                          <div className="relative">
                            <Input
                              id="acc-pwd"
                              type={showPassword ? 'text' : 'password'}
                              placeholder="Password sicura"
                              value={newAccesso.password}
                              onChange={(e) =>
                                setNewAccesso((p) => ({ ...p, password: e.target.value }))
                              }
                              disabled={creatingAccesso}
                              required
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-1 top-1/2 -translate-y-1/2"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>

                        <Button
                          type="submit"
                          disabled={
                            creatingAccesso ||
                            !newAccesso.condominio_id ||
                            !newAccesso.username ||
                            !newAccesso.password
                          }
                          className="w-full gap-2"
                        >
                          {creatingAccesso ? (
                            <>
                              <RefreshCw className="h-4 w-4 animate-spin" /> Creazione...
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4" /> Crea accesso
                            </>
                          )}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>

                  <Card className="border-dashed">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Shield className="h-4 w-4 text-primary" />
                        Portale Condòmini
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm text-muted-foreground">
                      <p>
                        Gli accessi creati qui permettono ai condòmini di visualizzare le analisi
                        completate del loro condominio tramite il portale pubblico.
                      </p>
                      <Separator />
                      <div className="flex gap-2">
                        <span className="font-mono text-primary font-bold">1.</span>
                        <span>Seleziona il condominio per cui creare l'accesso</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-mono text-primary font-bold">2.</span>
                        <span>Scegli un username univoco (es. nome condominio)</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-mono text-primary font-bold">3.</span>
                        <span>Imposta una password sicura</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-mono text-primary font-bold">4.</span>
                        <span>Comunica le credenziali ai condòmini</span>
                      </div>
                      <Separator />
                      <p className="text-xs">
                        🔐 Le password sono salvate in chiaro per permettere il recupero. Usa password
                        uniche per ogni condominio.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="analisi">
              <Card>
                <CardHeader>
                  <CardTitle>Analisi Problematiche</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Analisi in stato bozza o errore che potrebbero richiedere attenzione
                  </p>
                </CardHeader>
                <CardContent>
                  {analisiProblematiche.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-success" />
                      <p>Nessuna analisi problematica trovata</p>
                    </div>
                  ) : (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Titolo</TableHead>
                            <TableHead>Condominio</TableHead>
                            <TableHead className="text-center">Stato</TableHead>
                            <TableHead>Anno</TableHead>
                            <TableHead>Creata</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {analisiProblematiche.map((analisi) => {
                            const condo = condomini.find((c) => c.id === analisi.condominio_id);
                            return (
                              <TableRow key={analisi.id}>
                                <TableCell className="font-medium">
                                  {analisi.titolo}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {condo?.nome || '—'}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge
                                    variant={analisi.stato === 'errore' ? 'destructive' : 'secondary'}
                                  >
                                    {analisi.stato === 'errore' ? 'Errore' : 'Bozza'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {analisi.anno}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {formatDate(analisi.created_at)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="gap-1 text-destructive hover:text-destructive"
                                    onClick={() => setDeleteAnalisiId(analisi.id)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    Elimina
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Dialog credenziali generate */}
      <Dialog open={!!credentialsResult} onOpenChange={() => setCredentialsResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Credenziali Generate
            </DialogTitle>
          </DialogHeader>
          {credentialsResult && (
            <div className="space-y-4">
              {credentialsResult.emailSent ? (
                <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md p-3">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  Email con credenziali inviata con successo a {credentialsResult.email}
                </div>
              ) : (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Email non inviata</p>
                    <p className="text-xs mt-1">Il dominio Resend non è verificato. Copia le credenziali e invialo manualmente all&apos;utente.</p>
                  </div>
                </div>
              )}
              <div className="bg-muted rounded-md p-4 space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Email</p>
                  <p className="font-medium text-sm">{credentialsResult.email}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Password</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-background border rounded px-3 py-2 text-sm font-mono font-bold">{credentialsResult.password}</code>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => {
                        navigator.clipboard.writeText(credentialsResult.password);
                        toast.success('Password copiata');
                      }}
                    >
                      <Copy className="h-3 w-3" />
                      Copia
                    </Button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                La password è già stata impostata nel sistema. L&apos;utente può accedere subito con le credenziali indicate.
              </p>
              <Button className="w-full" onClick={() => setCredentialsResult(null)}>Chiudi</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Crea Nuovo Utente
            </DialogTitle>
          </DialogHeader>
          <CreateUserForm
            onSuccess={() => {
              setShowCreateUserDialog(false);
              fetchAdminData();
            }}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteAccessoId} onOpenChange={() => setDeleteAccessoId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questo accesso condòmino? L'operazione non può essere
              annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAccesso} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteAnalisiId} onOpenChange={() => setDeleteAnalisiId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione analisi</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questa analisi? Verranno eliminate anche tutte le voci di spesa, KPI e dati correlati. L'operazione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAnalisi} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
